import { stopConversationSubAgents } from "./stopConversationSubAgents";
import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";
import { insertUserInterruptEntry } from "./utils";
import { clientGeneratableIdSchema } from "../id";

export const interruptChatSchema = z.object({
  entryId: clientGeneratableIdSchema,
  chatId: z.string(),
  branchId: z.string(),
});

export const interruptChat = mutator({
  schema: interruptChatSchema,
  resolve: {
    branch: { kind: "branch", from: "branchId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.branch.workflow.spaceId,
    },
  ],
  fn: async ({ tx, ctx, args: { entryId, chatId, branch } }) => {
    const [chat] = await tx.run(
      zql.chats
        .where("id", chatId)
        .related("commit", (c) => c.where("branchId", branch.id))
    );
    if (!chat?.commit) throw userFacingError("Chat not found on branch");
    if (chat.commit.id !== branch.headCommitId && chat.respondingAt === null)
      throw userFacingError("Cannot interrupt an inactive historical chat");

    const [headChat] = await tx.run(
      zql.chats.where("commitId", branch.headCommitId ?? "")
    );
    if (!headChat) throw userFacingError("Chat not found on branch");

    const createdAt = Date.now();

    const lastEntries = await tx.run(
      zql.chatEntries
        .whereExists("chat", (c) =>
          c.whereExists("commit", (commit) =>
            commit.where("branchId", branch.id)
          )
        )
        .orderBy("createdAt", "desc")
        .limit(1)
    );
    const previousEntryId = lastEntries[0]?.id ?? null;

    await insertUserInterruptEntry(tx, {
      id: entryId,
      chatId: headChat.id,
      createdAt,
      previousEntryId,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.workflowBranches.update({
      id: branch.id,
      lastChatEntryAt: createdAt,
    });

    await tx.mutate.chats.update({
      id: chatId,
      awaitingAction: false,
      respondingAt: null,
    });

    await tx.mutate.chats.update({
      id: headChat.id,
      awaitingAction: false,
      lastChatEntryType: "UserInterrupt",
    });

    await tx.mutate.workflows.update({
      id: branch.workflowId,
      updatedAt: createdAt,
    });
    await stopConversationSubAgents(
      tx,
      { branchId: branch.id },
      chat.respondingAt
    );
  },
});
